<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList } from "@unio/saltbox-ui"
import { computed, ref } from "vue"

import DeleteWithReassignModal from "@/components/DeleteWithReassignModal.vue"
import DetailRow from "@/components/DetailRow.vue"
import ItemTypeFormSlideover from "@/components/ItemTypeFormSlideover.vue"
import ListItemRow from "@/components/ListItemRow.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import ResourceShowHeader from "@/components/ResourceShowHeader.vue"
import { useNavbar } from "@/composables/useNavbar"
import { colorHex } from "@/lib/colors"
import { pluralize } from "@/lib/format"
import { ICON, lucideIcon } from "@/lib/icons"
import { itemTypePath } from "@/routes"
import type { ItemSummary, ItemTypeSummary } from "@/types/models"

interface Props {
  item_type: ItemTypeSummary
  items: ItemSummary[]
  colors: string[]
  icons: string[]
  other_types: { id: string; display_name: string }[]
}

const props = defineProps<Props>()

const { title } = useNavbar({ title: props.item_type.display_name })

const editOpen = ref(false)
const deleteOpen = ref(false)
const deleting = ref(false)

const itemCount = computed(() => pluralize(props.items.length, "item"))

const patterns = computed(() => props.item_type.auto_detection_patterns ?? [])

const reassignOptions = computed(() =>
  props.other_types.map((t) => ({ label: t.display_name, value: t.id })),
)

function confirmDelete(mode: "nullify" | "reassign", reassignTo: string | null) {
  deleting.value = true
  const data: Record<string, string> = {}
  if (mode === "reassign" && reassignTo) {
    data.reassign_to = reassignTo
  }
  router.delete(itemTypePath({ id: props.item_type.id }), {
    data,
    onSuccess: () => (deleteOpen.value = false),
    onFinish: () => (deleting.value = false),
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <ResourceShowHeader
      :icon="lucideIcon(item_type.icon_class)"
      :icon-style="{ color: colorHex(item_type.color_class) }"
      :title="item_type.display_name"
      :subtitle="itemCount"
      @edit="editOpen = true"
      @delete="deleteOpen = true"
    />

    <p v-if="item_type.description" class="text-muted text-sm">{{ item_type.description }}</p>

    <SbList bordered separator>
      <DetailRow label="Status" :value="item_type.active ? 'Active' : 'Inactive'" />
      <DetailRow label="Sort Order" :value="String(item_type.sort_order)" />
    </SbList>

    <div v-if="patterns.length" class="flex flex-col gap-1 text-sm">
      <span class="text-muted">Detection Patterns</span>
      <div class="flex flex-wrap gap-1">
        <UBadge
          v-for="pattern in patterns"
          :key="pattern"
          color="neutral"
          variant="subtle"
          size="sm"
        >
          {{ pattern }}
        </UBadge>
      </div>
    </div>

    <SbList v-if="items.length" bordered separator>
      <ListItemRow v-for="item in items" :key="item.id" :item="item" variant="compact" />
    </SbList>

    <UEmpty
      v-else
      :icon="ICON.itemType"
      title="No items of this type"
      description="Items with this type will appear here."
    />
  </NarrowPage>

  <ItemTypeFormSlideover
    v-model:open="editOpen"
    :colors="colors"
    :icons="icons"
    :item-type="item_type"
  />

  <DeleteWithReassignModal
    v-model:open="deleteOpen"
    :title="`Delete ${item_type.display_name}?`"
    :loading="deleting"
    :child-count="items.length"
    :reassign-options="reassignOptions"
    nullify-label="Remove type from items"
    nullify-description="Items keep their data but won't have a type"
    reassign-label="Reassign to another type"
    reassign-description="Move all items before deleting"
    reassign-placeholder="Choose a type..."
    empty-message="This item type has no items and will be permanently deleted."
    @confirm="confirmDelete"
  />
</template>
